/**
 * Announcement banners REST surface (authored in the Customers app).
 *
 *   GET  /api/banners             — active banners for the current viewer
 *   POST /api/banners/:id/dismiss — per-user dismissal (idempotent)
 *   GET  /api/public/banners      — pre-login banners (sign-in page, no auth;
 *                                   dismissal is client-side via localStorage)
 *
 * Dismissal is optimistic: the banner leaves the cache immediately so the
 * bar collapses without waiting on the round-trip.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

/** One call-to-action on a banner. */
export interface BannerLinkDto {
  /** Web (http/https), phone (tel:), or email (mailto:) URI. */
  url: string
  /** Hyperlink display text; null falls back to "Learn more". */
  text: string | null
}

export interface BannerDto {
  id: string
  title: string
  body: string | null
  /**
   * LEGACY primary link, kept so this client still renders against a server
   * deployed before multi-link banners. Read `bannerDtoLinks`, not this.
   */
  link: string | null
  /** LEGACY display text for `link`. */
  linkText: string | null
  /** Ordered links; absent from servers older than the multi-link change. */
  links?: BannerLinkDto[]
  icon: string
  color: string
  dismissible: boolean
}

export const bannersKey = ['banners'] as const

export function useActiveBanners() {
  return useQuery({
    queryKey: bannersKey,
    queryFn: ({ signal }) => apiFetch<{ banners: BannerDto[] }>('/api/banners', { signal }),
    staleTime: 30_000,
  })
}

export const preLoginBannersKey = ['banners', 'pre-login'] as const

export function usePreLoginBanners() {
  return useQuery({
    queryKey: preLoginBannersKey,
    queryFn: ({ signal }) => apiFetch<{ banners: BannerDto[] }>('/api/public/banners', { signal }),
    staleTime: 30_000,
    // No retries: this is decoration on the page users MUST be able to reach.
    // If the backend is unhealthy, stay silent rather than pile retries onto
    // it from the sign-in screen — the component already renders nothing.
    retry: false,
  })
}

export function useDismissBanner() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (bannerId: string) =>
      apiFetch<{ ok: boolean }>(`/api/banners/${encodeURIComponent(bannerId)}/dismiss`, { method: 'POST' }),
    onMutate: async (bannerId: string) => {
      await qc.cancelQueries({ queryKey: bannersKey })
      const previous = qc.getQueryData<{ banners: BannerDto[] }>(bannersKey)
      if (previous) {
        qc.setQueryData(bannersKey, { banners: previous.banners.filter((b) => b.id !== bannerId) })
      }
      return { previous }
    },
    onError: (_error, _bannerId, context) => {
      if (context?.previous) {
        qc.setQueryData(bannersKey, context.previous)
      }
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: bannersKey })
    },
  })
}

/**
 * The links to render for a banner. Falls back to the legacy `link`/`linkText`
 * pair so this client works against a server deployed before `links` existed.
 */
export function bannerDtoLinks(banner: BannerDto): BannerLinkDto[] {
  if (banner.links && banner.links.length > 0) {
    return banner.links
  }
  return banner.link ? [{ url: banner.link, text: banner.linkText }] : []
}
